/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/arrow_right.svg";
import __asset_1 from "@res/icons/alert_complete.svg";
import __asset_2 from "@res/icons/alert_action.svg";
import __asset_3 from "@res/icons/alert_attention_needed.svg";
import __asset_4 from "@res/icons/clinic_open.svg";
import __asset_5 from "@res/icons/alert_pending.svg";
import __asset_6 from "@res/icons/comment_indication.svg";
import * as React from "react";
import {ReactNode} from "react";
import * as emotion from "emotion";
import {HGap} from "../../widget/HGap";
import {COLORS} from "@res/colors";
import {formatAlertDatetime, formatStatus} from "./util";
import {AlertFeedback, AlertStatus, DB_Alert, FeedbackCategory, getAlertDetails, getAlertTitle} from "@app/app-shared/alert";
import {_keys, AuditBy} from "@intuitionrobotics/ts-common";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/app-frontend/core/BaseComponent";
import {FONTS} from "@res/fonts";
import {AlertsModule} from "@modules/AlertsModule";
import {ICONS} from "@res/icons";
import {_BaseNodeRenderer, Adapter, AdapterBuilder, ItemToRender, MenuModule} from "@intuitionrobotics/thunderstorm/frontend";
import {MenuAndButton} from "@intuitionrobotics/thunderstorm/app-frontend/modules/menu/MenuAndButton";
import {StatusEntry} from "./StatusEntry";
import {CommentsModule} from "@modules/CommentsModule";
import {VGap} from "../../widget/VGap";
import dayjs from "../../../utils/dayjs";
// import {
// 	hideTooltip,
// 	showTooltip
// } from "../../widget/appTooltip";

const alertRowCSS = emotion.css(
    {
        width: "100%",
        minHeight: 73,
        boxSizing: "border-box",
        padding: "0 24px",
        justifyContent: "space-between",
        borderBottom: `1px solid #ececec`
    }
);

const alertRowAttentionCSS = emotion.css(
    {
        backgroundColor: "#fffaf0"
    }
);

export const headerStyle = emotion.css(
    {
        ":hover": {backgroundColor: COLORS.primary_blue_10, cursor: "pointer"},
        width: "fit-content",
        display: "flex",
        borderRadius: 4
    }
);

type AlertRowProps = {
    alert: DB_Alert<any>;
    onFeedbackSubmit: (feedback: AlertFeedback) => void
    feedbackCategories: { feedback: FeedbackCategory[] }
    onDetailsOpen?: () => void
}

export type AlertStatusChange = {
    status: AlertStatus
    alert: DB_Alert<any>;
    icon: ReactNode;
    onSelect: () => void;
    feedbackCategories: { feedback: FeedbackCategory[], lastUpdated?: AuditBy },
    onFeedbackSubmit: (feedback: AlertFeedback) => void;
}

type StatusMenuRm = {
    status: _BaseNodeRenderer<AlertStatusChange>
}

export const arrow = __asset_0;
export const complete = __asset_1;
export const action = __asset_2;
export const attention = __asset_3;
export const open = __asset_4;
export const pending = __asset_5;
export const comment = __asset_6;

export enum AlertStatusClinicDropdown {
    OPEN = "OPEN",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETE = "COMPLETE"
}

export class AlertRow
    extends BaseComponent<AlertRowProps> {

    constructor(props: AlertRowProps) {
        super(props);
    }

    private renderDescriptionRow = () => {
        const details = getAlertDetails(this.props.alert);

        return <>{details.split("\\n").map(detail => {
            return <div key={detail} style={{display: "flex", alignItems: "center"}}>
                {detail && FONTS.reg_14_secondary_dark(detail.replace("_", " "))}
            </div>;
        })}</>;
    };

    private renderHeaderRow = () => {
        const title = getAlertTitle(this.props.alert);
        return <div className={"ll_h_c"}>
            {this.needsFeedback() ? FONTS.medium_14(title) : FONTS.reg_14(title)}
        </div>;
    };

    private resolveIcon = (status: AlertStatus): ReactNode => {

        switch (status) {
            case AlertStatus.NEW:
            case AlertStatus.OPEN:
                return <img alt={"open"} src={open} height={20}/>;
            case AlertStatus.COMPLETE:
                return <img alt={"complete"} src={complete} height={20}/>;
            case AlertStatus.IN_PROGRESS:
                return <img alt={"action"} src={action} height={20}/>;
            default:
                return <div/>;
        }

    };

    private rendererMap: StatusMenuRm = {
        status: StatusEntry
    };

    private renderStatusHeader = () => {
        const status = this.props.alert.status;
        // if (status === AlertStatusClinic.NEW)
        // 	status = AlertStatusClinic.OPEN;

        return <div className={headerStyle}>
            {this.resolveIcon(status)}
            <HGap h={8}/>
            {FONTS.reg_14(formatStatus(status), {paddingTop: "1px"})}
            {<div style={{paddingLeft: "8px", paddingTop: "1px"}}>{this.iconStatusChange()}</div>}
        </div>;
    };


    private iconStatusChange = () => {
        return <div
            style={{backgroundColor: COLORS.primary_blue_10, width: "15px", height: "15px", borderRadius: 20}}>
            {ICONS.icon_arrow_down(COLORS.primary_dark_blue, 16)}
        </div>;
    };

    private buildElements = (): ItemToRender<StatusMenuRm>[] => {
        return _keys(AlertStatusClinicDropdown).map(_status => {
            return {
                item: {
                    status: AlertStatus[_status],
                    alert: this.props.alert,
                    icon: this.resolveIcon(AlertStatus[_status]),
                    onSelect: () => {
                        MenuModule.hide("alert_status_change");
                    },
                    feedbackCategories: this.props.feedbackCategories,
                    onFeedbackSubmit: this.onFeedbackSubmit
                },
                type: "status"
            };
        });
    };

    private renderDropdownButton = (): ReactNode => {
        const adapter: Adapter = AdapterBuilder()
            .list()
            .multiRender(this.rendererMap)
            .setData(this.buildElements())
            .build();

        return <div onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
        }}>
            <MenuAndButton
                id={"alert_status_change"}
                iconOpen={this.renderStatusHeader()}
                iconClosed={this.renderStatusHeader()}
                adapter={adapter}
                css={{width: "fit-content"}}/>
        </div>;
    };

    private renderDate = () => {
        let date, time, text;
        const nComments = CommentsModule.getNumberOfComments(this.props.alert._id) || 0;
        let updatedDate, updatedTime;
        if (AlertsModule.isNew(this.props.alert)) {
            const formatted = formatAlertDatetime(this.props.alert._created.auditAt.timestamp);
            date = formatted.date;
            time = dayjs(this.props.alert._created.auditAt.timestamp).format("hh:mm A");
            text = "Created";
        } else {
            const formatted = formatAlertDatetime(this.props.alert._updated.auditAt.timestamp);
            date = formatted.date;
            time = dayjs(this.props.alert._updated.auditAt.timestamp).format("hh:mm A");
            text = "Last update";

            // Display both created and updated times.
            const updatedTimeFormatted = formatAlertDatetime(this.props.alert._created.auditAt.timestamp);
            updatedDate = updatedTimeFormatted.date;
            updatedTime = dayjs(this.props.alert._created.auditAt.timestamp).format("hh:mm A");
        }
        return <div className={`ll_v_l`}>
            {
                !AlertsModule.isNew(this.props.alert) &&
                <div>
                    {FONTS.reg_11_secondary_dark(`Created: ${updatedDate} | ${updatedTime}`, {whiteSpace: "nowrap"})}
                </div>
            }
            <div className={`ll_h_c`} style={{paddingTop: 2}}>
                <div>
                    {FONTS.reg_11_secondary_dark(`${text}: ${date} | ${time}${nComments !== 0 ? " |" : ""}`, {whiteSpace: "nowrap"})}
                </div>
                <HGap h={5}/>
                {
                    nComments !== 0 &&
                    <div className={`ll_h_r`}>
                        <div style={{marginRight: 5}}>
                            {FONTS.reg_12(`${nComments}`)}
                        </div>
                        <div style={{height: 15, width: 15}}>
                            <img alt={"comment"} src={comment} height={15} width={15}/>
                        </div>
                    </div>
                }
            </div>
        </div>;
    };

    private renderContentRow = () => {
        return <div className={"ll_h_c"} style={{flex: "10", padding: "10px 0"}}>
            <div className={"ll_v_s"}>
                {this.renderDropdownButton()}
                <VGap v={6}/>
                <div onClick={() => {
                    // this.props.onDetailsOpen();
                }}>
                    {this.renderHeaderRow()}
                    <VGap v={6}/>
                    {this.renderDescriptionRow()}
                    <VGap v={6}/>
                    {this.renderDate()}
                </div>
            </div>
        </div>;
    };

    private onFeedbackSubmit = (feedback: AlertFeedback) => {
        this.props.onFeedbackSubmit(feedback);
    };

    private renderArrow = () => {
        return <div className={"ll_h_c clickable"} style={{flex: "1", padding: "10px 0"}} onClick={() => {
            // this.props.onDetailsOpen();
        }}>
            <div style={{backgroundColor: COLORS.very_light_blue, width: "25px", height: "25px", borderRadius: 6}}>
                <div style={{paddingLeft: "10px", paddingTop: "3px"}}>
                    <img alt={"details"} src={arrow} height={12}/>
                </div>
            </div>
        </div>;
    };

    private needsFeedback = () => AlertsModule.needsFeedback(this.props.alert);

    private isNew = () => AlertsModule.isNew(this.props.alert);

    render() {
        return <div
            key={this.props.alert._id}
            className={`${alertRowCSS} ${this.isNew() && alertRowAttentionCSS} ll_h_s`}
            onClick={() => this.props.onDetailsOpen?.()}
        >
            {this.renderContentRow()}
            <HGap h={30}/>
            {!!this.props.onDetailsOpen && this.renderArrow()}
        </div>;
    }

}
